import React, { useMemo } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { Box, Typography } from "@mui/material";
import { withCommas } from "./numberFormat";

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

function formatCurrencyShort(value, { asBps = false, navNumber } = {}) {
  if (value == null || isNaN(value)) return "—";
  if (asBps && navNumber) {
    return `${((value / navNumber) * 10000).toFixed(1)} bps`;
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${withCommas(abs / 1e6)}M`;
  if (abs >= 1e3) return `${sign}$${withCommas(abs / 1e3, 0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

export default function HistoricalVarChart({ data, onBarClick, showBpsOfNav = false, navNumber = null }) {
  const option = useMemo(() => {
    if (!data || data.length === 0) return null;

    const dates = data.map((d) => d.valuation_date);
    const var1 = data.map((d) => d.var_1pct);
    const var5 = data.map((d) => d.var_5pct);

    const showZoom = dates.length > 30;

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (params) => {
          const dateStr = params[0]?.axisValue;
          const idx = dates.indexOf(dateStr);
          const point = data[idx];
          if (!point) return dateStr;
          return `<b>${dateStr}</b><br/>VAR 1%: <b>${formatCurrencyShort(point.var_1pct, { asBps: showBpsOfNav, navNumber })}</b> (Sim #${point.sim_1pct})<br/>VAR 5%: <b>${formatCurrencyShort(point.var_5pct, { asBps: showBpsOfNav, navNumber })}</b> (Sim #${point.sim_5pct})<br/><span style="font-size:11px;color:#888">Click for breakdown</span>`;
        },
      },
      legend: {
        data: ["VAR 1%", "VAR 5%"],
        top: 0,
        textStyle: { fontSize: 11 },
      },
      grid: {
        left: 60,
        right: 20,
        top: 30,
        bottom: showZoom ? 70 : 40,
      },
      xAxis: {
        type: "category",
        data: dates,
        axisLabel: {
          fontSize: 10,
          rotate: dates.length > 15 ? 45 : 0,
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#ddd" } },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          fontSize: 11,
          formatter: (v) => formatCurrencyShort(v, { asBps: showBpsOfNav, navNumber }),
        },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      dataZoom: showZoom
        ? [
            {
              type: "slider",
              start: Math.max(0, ((dates.length - 30) / dates.length) * 100),
              end: 100,
              height: 20,
              bottom: 10,
            },
          ]
        : [],
      series: [
        {
          name: "VAR 1%",
          type: "line",
          data: var1,
          smooth: true,
          lineStyle: { width: 2 },
          itemStyle: {
            color: "#BE9B53",
          },
          emphasis: {
            itemStyle: { shadowBlur: 8, shadowColor: "rgba(0,0,0,0.2)" },
          },
        },
        {
          name: "VAR 5%",
          type: "line",
          data: var5,
          smooth: true,
          lineStyle: { width: 2 },
          itemStyle: {
            color: "#91B6C0",
          },
          emphasis: {
            itemStyle: { shadowBlur: 8, shadowColor: "rgba(0,0,0,0.2)" },
          },
        },
      ],
    };
  }, [data, showBpsOfNav, navNumber]);

  if (!option) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="caption" color="text.disabled">
          No historical data available
        </Typography>
      </Box>
    );
  }

  const handleClick = (params) => {
    if (params.dataIndex == null || !onBarClick) return;
    const point = data[params.dataIndex];
    if (!point) return;
    const isVar1 = params.seriesName === "VAR 1%";
    onBarClick(
      point.valuation_date,
      isVar1 ? point.sim_1pct : point.sim_5pct,
      isVar1 ? point.var_1pct : point.var_5pct,
      isVar1 ? "p1" : "p5",
    );
  };

  return (
    <Box sx={{ width: "100%", minHeight: 0 }}>
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        style={{ height: "clamp(220px, 30vw, 320px)", width: "100%" }}
        onEvents={{ click: handleClick }}
        notMerge
      />
    </Box>
  );
}
